/**
 * RTK Query endpoints for the projects service (@cluster/projects).
 * Base path after the dev proxy + nginx rewrite strips `/api`: `/projects`.
 */
import { apiSlice } from './apiSlice';
import type {
  Project,
  ProjectResource,
  ProjectResourceType,
  UserGroup,
} from '@/types';

function data<T>(response: { data: T }): T {
  return response.data;
}

export const projectsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProjects: builder.query<Project[], void>({
      query: () => '/projects',
      transformResponse: data<Project[]>,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Project' as const, id: _id })),
              { type: 'Project', id: 'LIST' },
            ]
          : [{ type: 'Project', id: 'LIST' }],
    }),

    getProject: builder.query<Project, string>({
      query: (id) => `/projects/${id}`,
      transformResponse: data<Project>,
      providesTags: (_result, _error, id) => [{ type: 'Project', id }],
    }),

    createProject: builder.mutation<Project, Partial<Project>>({
      query: (body) => ({ url: '/projects', method: 'POST', body }),
      transformResponse: data<Project>,
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    updateProject: builder.mutation<Project, { id: string; body: Partial<Project> }>({
      query: ({ id, body }) => ({ url: `/projects/${id}`, method: 'PATCH', body }),
      transformResponse: data<Project>,
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'Project', id }, { type: 'Project', id: 'LIST' }]
          : [{ type: 'Project', id: 'LIST' }],
    }),

    deleteProject: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/projects/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    // ── Resources (attach by type) ────────────────────────────────────────
    getProjectResources: builder.query<
      ProjectResource[],
      { id: string; type?: ProjectResourceType }
    >({
      query: ({ id, type }) => ({
        url: `/projects/${id}/resources`,
        params: type ? { type } : undefined,
      }),
      transformResponse: data<ProjectResource[]>,
      providesTags: (result, _error, { id }) =>
        result
          ? [
              ...result.map((r) => ({ type: 'ProjectResource' as const, id: r._id })),
              { type: 'ProjectResource', id },
            ]
          : [{ type: 'ProjectResource', id }],
    }),

    attachProjectResource: builder.mutation<
      ProjectResource,
      { id: string; body: { type: ProjectResourceType; refId: string; name?: string; metadata?: Record<string, unknown> } }
    >({
      query: ({ id, body }) => ({
        url: `/projects/${id}/resources`,
        method: 'POST',
        body,
      }),
      transformResponse: data<ProjectResource>,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Project', id },
        { type: 'ProjectResource', id },
      ],
    }),

    detachProjectResource: builder.mutation<
      { success: boolean },
      { id: string; resourceId: string }
    >({
      query: ({ id, resourceId }) => ({
        url: `/projects/${id}/resources/${resourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Project', id },
        { type: 'ProjectResource', id },
      ],
    }),

    // ── User groups attached to a project ────────────────────────────────
    getProjectUserGroups: builder.query<UserGroup[], string>({
      query: (id) => `/projects/${id}/user-groups`,
      transformResponse: (response: { data: UserGroup[] }) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Project', id }],
    }),

    attachProjectUserGroup: builder.mutation<
      { success: boolean },
      { id: string; groupId: string }
    >({
      query: ({ id, groupId }) => ({
        url: `/projects/${id}/user-groups`,
        method: 'POST',
        body: { groupId },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Project', id }],
    }),

    detachProjectUserGroup: builder.mutation<
      { success: boolean },
      { id: string; groupId: string }
    >({
      query: ({ id, groupId }) => ({
        url: `/projects/${id}/user-groups/${groupId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Project', id }],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetProjectResourcesQuery,
  useAttachProjectResourceMutation,
  useDetachProjectResourceMutation,
  useGetProjectUserGroupsQuery,
  useAttachProjectUserGroupMutation,
  useDetachProjectUserGroupMutation,
} = projectsApi;